/** @fileoverview Composes tenant navigation, support actions, and account controls. */

import { ArrowLeft, History, MessageSquare } from 'lucide-react';
import { FabrikNavSupportActions } from '../AccountMenu';
import TenantSelector from '../TenantSelector';
import { Button } from '../ui/button';
import FabrikNavbar, { FabrikNavbarActions } from './FabrikNavbar';
import type { Tenant, User } from '../../types/identity';

type Props = {
  user?: User;
  tenants: Tenant[];
  selectedTenantId: string;
  tenant?: Tenant;
  platformHomeURL: string;
  creatingApp: boolean;
  isNewAppRoute: boolean;
  onSelectTenant: (id: string) => void;
  onGoToFabrikHome: () => void;
  isHistoryOpen: boolean;
  onToggleHistory: () => void;
};

export default function FabrikShellHeader({
  user,
  tenants,
  selectedTenantId,
  tenant,
  platformHomeURL,
  creatingApp,
  isNewAppRoute,
  onSelectTenant,
  onGoToFabrikHome,
  isHistoryOpen,
  onToggleHistory,
}: Props) {
  const creating = creatingApp || isNewAppRoute;

  return (
    <FabrikNavbar
      logoHref={platformHomeURL}
      leading={
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            className={`h-auto p-0 hover:bg-transparent ${
              creating
                ? 'text-base font-semibold text-app-text-muted hover:text-app-text'
                : 'text-base font-semibold text-app-text hover:text-app-text'
            }`}
            type="button"
            onClick={onGoToFabrikHome}
          >
            {creating && <ArrowLeft className="h-4 w-4" aria-hidden="true" />}
            Fabrik
          </Button>
          {creating && (
            <>
              <span className="text-base text-app-text-subtle">/</span>
              <span className="text-base font-semibold text-app-text">New app</span>
            </>
          )}
        </div>
      }
      center={
        <TenantSelector
          user={user}
          tenants={tenants}
          selectedTenantId={selectedTenantId}
          onSelect={onSelectTenant}
        />
      }
    >
      {user && (
        <FabrikNavbarActions user={user} tenant={tenant}>
          {creating ? (
            <Button
              type="button"
              variant="ghost"
              className="h-8 gap-2 px-2 text-sm text-app-text-muted hover:bg-app-surface-muted-strong hover:text-app-text"
              onClick={onToggleHistory}
            >
              {isHistoryOpen ? (
                <MessageSquare className="h-4 w-4" aria-hidden="true" />
              ) : (
                <History className="h-4 w-4" aria-hidden="true" />
              )}
              {isHistoryOpen ? 'Open Assistant' : 'Past conversations'}
            </Button>
          ) : (
            <FabrikNavSupportActions user={user} tenant={tenant} />
          )}
        </FabrikNavbarActions>
      )}
    </FabrikNavbar>
  );
}
